// Persistent ring buffer of recent app errors, included in support reports.
// Fed from the global handlers in VueData.logException.
import { sanitizeString } from '@/lib/support/support-report';

const STORAGE_KEY = 'admin_error_history';
const MAX_ENTRIES = 100;

export interface IErrorHistoryEntry {
  timestamp: number;
  source: string;
  message: string;
  stack?: string;
}

export function getErrorHistory(): IErrorHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as IErrorHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

export function recordErrorHistoryEntry(source: string, error: unknown): void {
  try {
    let message: string;
    let stack: string | undefined;

    if (error instanceof Error) {
      message = error.message;
      stack = error.stack ?? undefined;
    } else if (typeof error === 'string') {
      message = error;
    } else {
      try {
        message = JSON.stringify(error);
      } catch {
        message = String(error);
      }
    }

    const entry: IErrorHistoryEntry = {
      timestamp: Date.now(),
      source: sanitizeString(source, 200),
      message: sanitizeString(message ?? '', 2000),
      ...(stack ? { stack: sanitizeString(stack, 4000) } : {}),
    };

    // Newest first, capped.
    const next = [entry, ...getErrorHistory()].slice(0, MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Recording an error must never throw from an error path.
  }
}

export function clearErrorHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}
